import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, STATUS_LABEL, fmtDateTime, fmtEUR, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { summary, hasProduction, VERSAND_STATUS_LABEL } from "@/lib/produktion";

// Kleiner Produktionsfortschritt in der Auftragsliste (nur wenn Produktion/Versand erfasst)
const ProduktionMini = ({ order }) => {
  if (!hasProduction(order.positionen) && !order.versand_status) return null;
  const s = summary(order.positionen);
  const pct = s.menge ? Math.round(((s.fertig + s.versendet) / s.menge) * 100) : 0;
  return (
    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500" title={`${s.fertig + s.versendet}/${s.menge} fertig, ${s.versendet} versendet`} data-testid={`auftrag-prod-${order.id}`}>
      <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
      <span className="font-mono">{s.fertig + s.versendet}/{s.menge}</span>
      {order.versand_status && <span className="text-slate-700">{VERSAND_STATUS_LABEL[order.versand_status]}</span>}
    </div>
  );
};
import { toast } from "sonner";
import { Plus, Search, UserPlus, AlertTriangle } from "lucide-react";

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "neu", label: "Neu" },
  { key: "in_bearbeitung", label: "In Bearbeitung" },
  { key: "wartet_auf_zahlung", label: "Wartet auf Zahlung" },
  { key: "bezahlt", label: "Bezahlt" },
  { key: "storniert", label: "Storniert" },
];

const QUICK_ADD_VALUE = "__quick_add__";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Auftraege() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [nurMeine, setNurMeine] = useState(() => localStorage.getItem("auftraege_nur_meine") === "1");
  // Standard: Stornierte ausblenden (nur wenn nicht explizit nach „storniert“ gefiltert wird)
  const [ohneStorniert, setOhneStorniert] = useState(() => localStorage.getItem("auftraege_ohne_storniert") !== "0");
  const [ausgeblendet, setAusgeblendet] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kunden, setKunden] = useState([]);
  const [form, setForm] = useState({ kunde_id: "", titel: "", notiz_intern: "" });
  const [saving, setSaving] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({ name: "", email: "" });
  const [quickDupes, setQuickDupes] = useState([]);
  const [quickSaving, setQuickSaving] = useState(false);
  const user = getUser();

  const loadSeq = useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current; // nur die zuletzt gestartete Anfrage darf die Liste setzen (schnelles Umschalten der Filter)
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    const hideCancelled = ohneStorniert && !status;
    if (hideCancelled) params.ohne_storniert = true;
    const [r, cnt] = await Promise.all([
      api.get("/auftraege", { params }),
      hideCancelled ? api.get("/auftraege/stornierte-anzahl", { params: q ? { q } : {} }).catch(() => null) : Promise.resolve(null),
    ]);
    if (seq !== loadSeq.current) return;
    setAusgeblendet(cnt?.data?.anzahl || 0);
    let data = r.data;
    if (nurMeine && user) {
      data = data.filter((o) =>
        o.verantwortlich_user_id === user.id ||
        (o.team && [o.team.hauptverantwortlich_user_id, o.team.konstruktion_user_id, o.team.produktion_user_id, o.team.versand_user_id, o.team.buchhaltung_user_id, ...(o.team.watcher_user_ids || [])].includes(user.id))
      );
    }
    setList(data);
  };

  useEffect(() => { load();  }, [status, q, nurMeine, ohneStorniert]);
  useEffect(() => { localStorage.setItem("auftraege_nur_meine", nurMeine ? "1" : "0"); }, [nurMeine]);
  useEffect(() => { localStorage.setItem("auftraege_ohne_storniert", ohneStorniert ? "1" : "0"); }, [ohneStorniert]);

  const openCreate = async () => {
    const r = await api.get("/kunden");
    setKunden(r.data);
    setForm({ kunde_id: r.data[0]?.id || "", titel: "", notiz_intern: "" });
    setDialogOpen(true);
  };

  const onKundeSelect = (v) => {
    if (v === QUICK_ADD_VALUE) {
      setQuick({ name: "", email: "" });
      setQuickDupes([]);
      setQuickOpen(true);
      return;
    }
    setForm({ ...form, kunde_id: v });
  };

  const checkQuickDupes = async () => {
    if (!quick.email || !EMAIL_RE.test(quick.email)) { setQuickDupes([]); return; }
    try {
      const r = await api.post("/kunden/duplicate-check", { email: quick.email, nachname: quick.name });
      setQuickDupes(r.data.duplicates || []);
    } catch { setQuickDupes([]); }
  };

  const saveQuick = async () => {
    const name = quick.name.trim();
    if (!name) { toast.error("Bitte Name angeben."); return; }
    if (!EMAIL_RE.test(quick.email.trim())) { toast.error("Bitte gültige E-Mail-Adresse angeben."); return; }
    setQuickSaving(true);
    try {
      const r = await api.post("/kunden/quick", { name, email: quick.email.trim() });
      toast.success(`Kunde ${r.data.kundennummer} angelegt (Daten unvollständig).`);
      setKunden((prev) => [r.data, ...prev]);
      setForm({ ...form, kunde_id: r.data.id });
      setQuickOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen.");
    } finally { setQuickSaving(false); }
  };

  const save = async () => {
    if (!form.kunde_id || !form.titel) { toast.error("Kunde und Titel erforderlich."); return; }
    setSaving(true);
    try {
      const r = await api.post("/auftraege", form);
      toast.success(`Auftrag ${r.data.auftragsnummer} angelegt.`);
      setDialogOpen(false);
      nav(`/auftraege/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const selectedKunde = kunden.find((k) => k.id === form.kunde_id);

  return (
    <div className="space-y-6" data-testid="auftraege-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Aufträge</h1>
          <p className="text-sm text-slate-500 mt-1">Alle Aufträge inkl. Preisfreigabe und Zahlungsstatus.</p>
        </div>
        <Button onClick={openCreate} data-testid="auftrag-add-button"><Plus className="w-4 h-4 mr-2" /> Auftrag anlegen</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key || "alle"}
            onClick={() => setStatus(f.key)}
            data-testid={`filter-${f.key || "alle"}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${status === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={() => setNurMeine(!nurMeine)} data-testid="filter-nur-meine"
          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${nurMeine ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
          Nur meine
        </button>
        <label htmlFor="toggle-ohne-storniert" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border bg-white border-slate-200 text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors focus-within:ring-2 focus-within:ring-blue-500 ${status === "storniert" ? "opacity-60" : ""}`}
          title={status === "storniert" ? "Status-Filter „Storniert“ aktiv – stornierte Aufträge werden angezeigt." : "Stornierte Aufträge (alle Storno-Arten) ausblenden"}>
          <input id="toggle-ohne-storniert" type="checkbox" checked={ohneStorniert} onChange={(e) => setOhneStorniert(e.target.checked)}
            className="w-4 h-4 accent-slate-900" data-testid="filter-ohne-storniert" />
          Stornierte ausblenden
          {ohneStorniert && !status && ausgeblendet > 0 && !nurMeine && (
            <span className="text-xs font-normal text-slate-500" data-testid="filter-ohne-storniert-count">{ausgeblendet} ausgeblendet</span>
          )}
        </label>
        <div className="relative flex-1 min-w-[240px] ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche nach Titel oder Auftragsnr.…" className="pl-9" data-testid="auftrag-search-input" />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="auftrag-table">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="px-4 py-3 font-medium text-slate-600">Auftragsnr.</th>
                <th className="px-4 py-3 font-medium text-slate-600">Titel</th>
                <th className="px-4 py-3 font-medium text-slate-600">Kunde</th>
                <th className="px-4 py-3 font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 font-medium text-slate-600 text-right">Summe brutto</th>
                <th className="px-4 py-3 font-medium text-slate-600">Angelegt</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Keine Aufträge gefunden.</td></tr>
              )}
              {list.map((a) => (
                <tr key={a.id} onClick={() => nav(`/auftraege/${a.id}`)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`auftrag-row-${a.auftragsnummer}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{a.auftragsnummer}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{a.titel}</td>
                  <td className="px-4 py-3 text-slate-700">{a.kunde_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /><ProduktionMini order={a} /></td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{fmtEUR(a.summe_brutto)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="auftrag-create-dialog">
          <DialogHeader><DialogTitle>Neuen Auftrag anlegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kunde</Label>
              <Select value={form.kunde_id} onValueChange={onKundeSelect}>
                <SelectTrigger data-testid="auftrag-kunde-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={QUICK_ADD_VALUE} data-testid="auftrag-kunde-quickadd-option">
                    <span className="flex items-center gap-2 text-blue-700 font-medium"><UserPlus className="w-4 h-4" /> Neuer Kunde (Quick-Add)</span>
                  </SelectItem>
                  {kunden.map((k) => (
                    <SelectItem key={k.id} value={k.id}>
                      {k.kundennummer} — {k.firma || `${k.vorname} ${k.nachname}`.trim()}
                      {k.data_complete === false && <span className="ml-2 text-amber-600 text-xs">· Daten unvollständig</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedKunde && selectedKunde.data_complete === false && (
                <p className="text-xs text-amber-700 mt-1" data-testid="auftrag-kunde-incomplete-hint">
                  Kundendaten werden beim ersten Portalbesuch ergänzt.
                </p>
              )}
            </div>
            <div>
              <Label>Titel</Label>
              <Input value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} data-testid="auftrag-titel-input" />
            </div>
            <div>
              <Label>Interne Notiz</Label>
              <Textarea rows={3} value={form.notiz_intern} onChange={(e) => setForm({ ...form, notiz_intern: e.target.value })} data-testid="auftrag-notiz-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="auftrag-save-button">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-md" data-testid="kunde-quickadd-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-600" /> Neuer Kunde (Quick-Add)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Nur Name und E-Mail sind nötig. Die restlichen Daten ergänzt der Kunde selbst im Zahlungs-Portal.</p>
            {quickDupes.length > 0 && (
              <Alert className="bg-amber-50 border-amber-200 text-amber-900" data-testid="quickadd-duplicate-warning">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  <div className="font-semibold">Möglicher Duplikat ({quickDupes.length}):</div>
                  <ul className="list-disc pl-5 mt-1 text-sm">
                    {quickDupes.map((d) => <li key={d.id}>{d.kundennummer} — {d.firma || `${d.vorname} ${d.nachname}`.trim()} · {d.email}</li>)}
                  </ul>
                  <div className="text-xs mt-1">Anlegen trotzdem möglich.</div>
                </AlertDescription>
              </Alert>
            )}
            <div>
              <Label>Name *</Label>
              <Input
                autoFocus
                value={quick.name}
                onChange={(e) => setQuick({ ...quick, name: e.target.value })}
                placeholder="z. B. Max Mustermann"
                data-testid="quickadd-name-input"
              />
              <p className="text-xs text-slate-400 mt-1">Mit Leerzeichen → Vor-/Nachname wird aufgeteilt.</p>
            </div>
            <div>
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={quick.email}
                onChange={(e) => setQuick({ ...quick, email: e.target.value })}
                onBlur={checkQuickDupes}
                placeholder="kunde@example.de"
                data-testid="quickadd-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickOpen(false)}>Abbrechen</Button>
            <Button onClick={saveQuick} disabled={quickSaving} data-testid="quickadd-save-button">
              Kunde anlegen & auswählen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
